import { FC, useState, useMemo } from 'react';
import { Box, Typography, IconButton } from '@mui/material';
import DatePicker, { registerLocale } from 'react-datepicker';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { enUS } from 'date-fns/locale';

import 'react-datepicker/dist/react-datepicker.css';
import styles from './Calendar.module.scss';
import { Trip } from '../../../../../types/trip.types';
import { formatDateToLocal } from '../../../../../utils/dateUtils';

const customLocale = {
  ...enUS,
  localize: {
    ...enUS.localize,
    day: (n: number) => ['SUN', 'MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT'][n],
  },
};

registerLocale('custom', customLocale);

interface CalendarProps {
  trips?: Trip[];
  selectedDate?: Date;
  onDateSelect?: (date: Date | null) => void;
}

const Calendar: FC<CalendarProps> = ({ trips = [], selectedDate = new Date(), onDateSelect }) => {
  const [currentDate, setCurrentDate] = useState(selectedDate);
  const [showMonthYearPicker, setShowMonthYearPicker] = useState(false);

  const tripStartDates = useMemo(() => {
    const datesWithTrips = new Map<string, Trip[]>();

    trips.forEach(trip => {
      const startDate = new Date(trip.start_date);
      const dateKey = formatDateToLocal(startDate);

      if (!datesWithTrips.has(dateKey)) {
        datesWithTrips.set(dateKey, []);
      }
      datesWithTrips.get(dateKey)!.push(trip);
    });

    return datesWithTrips;
  }, [trips]);

  const hasTrips = (date: Date): boolean => {
    const dateKey = formatDateToLocal(date);
    return tripStartDates.has(dateKey) && tripStartDates.get(dateKey)!.length > 0;
  };

  const handleDateChange = (date: Date | null) => {
    if (date) {
      setCurrentDate(date);
      onDateSelect?.(date);
      if (showMonthYearPicker) {
        setShowMonthYearPicker(false);
      }
    }
  };

  const handleMonthChange = (date: Date) => {
    console.log('Month changed to:', date);
    setCurrentDate(date);
  };

  const toggleMonthYearPicker = () => {
    setShowMonthYearPicker(!showMonthYearPicker);
  };

  const CustomHeader = ({
    date,
    decreaseMonth,
    increaseMonth,
    prevMonthButtonDisabled,
    nextMonthButtonDisabled,
  }: any) => {
    return (
      <Box className={styles.calendarHeader}>
        <Box
          className={styles.headerLeft}
          onClick={toggleMonthYearPicker}
        >
          <Typography
            variant="h9Semibold"
            className={styles.monthYear}
          >
            {date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </Typography>
          <ChevronRightIcon className={`${styles.dropdownIcon} ${showMonthYearPicker ? styles.rotated : ''}`} />
        </Box>

        <Box className={styles.headerRight}>
          <IconButton
            onClick={() => {
              if (decreaseMonth) decreaseMonth();
            }}
            className={styles.navButton}
            size="small"
            disabled={prevMonthButtonDisabled}
          >
            <ChevronLeftIcon />
          </IconButton>

          <IconButton
            onClick={() => {
              if (increaseMonth) increaseMonth();
            }}
            className={styles.navButton}
            size="small"
            disabled={nextMonthButtonDisabled}
          >
            <ChevronRightIcon />
          </IconButton>
        </Box>
      </Box>
    );
  };

  return (
    <Box className={styles.calendarWrapper}>
      <DatePicker
        selected={currentDate}
        onChange={handleDateChange}
        onMonthChange={handleMonthChange}
        inline
        showPopperArrow={false}
        calendarClassName={styles.customTripCalendar}
        renderCustomHeader={CustomHeader}
        locale="custom"
        showMonthYearPicker={showMonthYearPicker}
        dayClassName={date => {
          const hasTrip = hasTrips(date);
          const isToday = date.toDateString() === new Date().toDateString();

          let classes = 'customDay';
          if (hasTrip) classes += ' dayWithTrip';
          if (isToday) classes += ' dayToday';

          return classes;
        }}
      />
    </Box>
  );
};

export default Calendar;
